import { BaseModal } from "@/components/modal/base-modal";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { ReactNode } from "react";

interface AmendSheetShellProps {
  isOpen: boolean;
  onDismiss: () => void;
  /** Sheet content height in px from the Figma frame (362 calories, 566 macros). */
  contentHeightPx: number;
  children: ReactNode;
}

/**
 * White bottom sheet used by the onboarding Amend flows ("Action sheet" in
 * Figma 983:5848 / 983:5865): 56px top radius, drag handle, fixed height
 * expressed as a fraction of the viewport.
 */
export const AmendSheetShell = ({
  isOpen,
  onDismiss,
  contentHeightPx,
  children,
}: AmendSheetShellProps) => {
  const viewportHeight = useViewportHeight();
  const breakpoint =
    viewportHeight > 0 ? Math.min(0.95, Math.max(0.1, contentHeightPx / viewportHeight)) : 0.5;

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss
      handle
      scrollY={false}
      initialBreakpoint={breakpoint}
      breakpoints={[0, breakpoint]}
      className="[&::part(handle)]:h-1 [&::part(handle)]:w-[54px] [&::part(handle)]:bg-[#d9d9d9]"
      styles={{
        modal: { "--background": "#ffffff" } as React.CSSProperties,
        content: { "--background": "#ffffff" } as React.CSSProperties,
      }}
    >
      <div className="safe-area-bottom flex flex-col items-center px-9 pt-12 pb-10">{children}</div>
    </BaseModal>
  );
};

interface ConfirmChangeButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isSaving?: boolean;
}

/** Dark purple gradient pill from the Figma sheets ("Button Pill Tall"). */
export const ConfirmChangeButton = ({ onClick, disabled, isSaving }: ConfirmChangeButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isSaving}
    className="flex h-[50px] w-full max-w-[323px] items-center justify-center rounded-[40px] bg-[linear-gradient(to_bottom,#513775,#43246e)] text-base font-medium text-white shadow-md disabled:opacity-50"
  >
    {isSaving ? "Saving..." : "Confirm Change"}
  </button>
);
